'use client';

import { useEffect, useState, useRef } from 'react';
import Script from 'next/script';

export interface MakeFormsEmbedProps {
  sourceId?: string;
  rootId?: string;
  className?: string;
  eventName?: string;
  linkedin_conversion_id?: string;
  resourceLink?: string;
  confirmation_page_url?: string;
  formHeader?: string;
}

const DEFAULT_CONFIRMATION_URL = 'https://www.contentstack.com/request-demo/thank-you';

declare global {
  interface Window {
    makeforms?: {
      Embed: new (config: { sourceId: string; root: string }) => {
        build: () => void;
      };
    };
    dataLayer?: Record<string, unknown>[];
  }
}

export default function MakeFormsEmbed({
  sourceId = '69b7eb7289c0ad83ca629dd5',
  rootId = 'epzubrnmc',
  className = 'makeforms-js-embed',
  eventName,
  linkedin_conversion_id,
  resourceLink,
  confirmation_page_url = DEFAULT_CONFIRMATION_URL,
  formHeader,
}: MakeFormsEmbedProps) {
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [formInitialized, setFormInitialized] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const initAttempted = useRef(false);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.makeforms) {
      setScriptLoaded(true);
    }
  }, []);

  const attachFormTracking = () => {
    const interval = setInterval(() => {
      const webComponent = document.querySelector(
        '[id^="form-design"]'
      ) as HTMLElement & { shadowRoot?: ShadowRoot };

      if (webComponent?.shadowRoot) {
        clearInterval(interval);

        const shadowRoot = webComponent.shadowRoot;
        const urlParams = new URLSearchParams(window.location.search);
        const trackingParams = [
          'utm_source',
          'utm_medium',
          'utm_campaign',
          'utm_term',
          'utm_content',
          'gclid',
        ];

        trackingParams.forEach((param) => {
          const value = urlParams.get(param);
          if (!value) return;

          const hiddenField = shadowRoot.querySelector(
            `input[name="${param}"]`
          ) as HTMLInputElement | null;
          if (hiddenField) {
            hiddenField.value = value;
          }
        });

        const submitBtn = shadowRoot.querySelector('button');
        if (!submitBtn) return;

        submitBtn.addEventListener('click', () => {
          const inputs = shadowRoot.querySelectorAll('input');
          const selects = shadowRoot.querySelectorAll('select');
          const textareas = shadowRoot.querySelectorAll('textarea');

          const emailInput = Array.from(inputs).find((input: Element) =>
            (input as HTMLInputElement)
              .getAttribute('data-label-analytics')
              ?.toLowerCase()
              .includes('email')
          ) as HTMLInputElement | undefined;

          if (!emailInput?.value?.trim()) return;

          const data: Record<string, string> = {
            first_name: '',
            last_name: '',
            email: '',
            company: '',
            phone: '',
            country: '',
            region: '',
            subject: '',
          };

          inputs.forEach((input: Element) => {
            const el = input as HTMLInputElement;
            const key = el.getAttribute('data-label-analytics');
            const value = el.value;

            if (key && value) {
              const normalizedKey = key
                .toLowerCase()
                .replace(/\s+/g, '_')
                .replace(/[^a-z0-9_]/g, '');

              if (normalizedKey.includes('email')) data.email = value;
              if (normalizedKey.includes('first') && normalizedKey.includes('name'))
                data.first_name = value;
              if (normalizedKey.includes('last') && normalizedKey.includes('name'))
                data.last_name = value;
              if (normalizedKey.includes('company')) data.company = value;
              if (normalizedKey.includes('phone')) data.phone = value;
            }
          });

          const hasPhoneNumberField = Array.from(inputs).some(
            (input: Element) =>
              (input as HTMLInputElement).getAttribute('data-label-analytics') === 'Phone Number'
          );
          const countrySelectIndex = hasPhoneNumberField ? 1 : 0;
          const regionSelectIndex = hasPhoneNumberField ? 2 : 1;

          if (selects[countrySelectIndex]) {
            const sel = selects[countrySelectIndex] as HTMLSelectElement;
            if (sel.value) data.country = sel.value;
          }
          if (selects[regionSelectIndex]) {
            const sel = selects[regionSelectIndex] as HTMLSelectElement;
            if (sel.value) data.region = sel.value;
          }

          if (textareas[0]) {
            const ta = textareas[0] as HTMLTextAreaElement;
            if (ta.value) data.subject = ta.value;
          }

          trackingParams.forEach((param) => {
            const value = urlParams.get(param);
            if (value) data[param] = value;
          });

          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: eventName || 'ResourceFormSubmit',
            formid: sourceId,
            form_data: {
              first_name: data.first_name,
              last_name: data.last_name,
              email: data.email,
              phone: data.phone,
              company: data.company,
              country: data.country,
              region: data.region || 'NA',
            },
          });

          if (linkedin_conversion_id && window.lintrk) {
            const conversionId = parseInt(linkedin_conversion_id, 10);
            if (!Number.isNaN(conversionId) && conversionId > 0) {
              window.lintrk('track', { conversion_id: conversionId });
            }
          }

          if (resourceLink) {
            if (resourceLink.match(/\.(pdf|zip|docx?|xlsx?|pptx?)$/i)) {
              const link = document.createElement('a');
              link.href = resourceLink;
              link.download = '';
              link.target = '_blank';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            } else {
              window.open(resourceLink, '_blank');
            }
          }
        });
      }
    }, 1000);
  };

  useEffect(() => {
    if (!scriptLoaded || formInitialized || initAttempted.current) return;

    initAttempted.current = true;

    let retryCount = 0;
    const maxRetries = 50;

    const initMakeForms = () => {
      const container = document.getElementById(rootId);

      if (!container) {
        retryCount++;
        if (retryCount < maxRetries) {
          setTimeout(initMakeForms, 100);
        }
        return;
      }

      const isVisible = container.offsetParent !== null;

      if (!isVisible) {
        retryCount++;
        if (retryCount < maxRetries) {
          setTimeout(initMakeForms, 100);
        }
        return;
      }

      if (window.makeforms) {
        try {
          new window.makeforms.Embed({
            sourceId: sourceId,
            root: rootId,
          }).build();

          setTimeout(() => {
            attachFormTracking();
          }, 2500);

          setFormInitialized(true);
        } catch (error) {
          console.error('MakeForms: Error building form:', error);
          initAttempted.current = false;
        }
      } else {
        retryCount++;
        if (retryCount < maxRetries) {
          setTimeout(initMakeForms, 100);
        } else {
          initAttempted.current = false;
        }
      }
    };

    const timeoutId = setTimeout(initMakeForms, 200);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [sourceId, rootId, scriptLoaded, formInitialized]);

  useEffect(() => {
    if (typeof window === 'undefined' || !confirmation_page_url) return;

    const handleHashRedirect = () => {
      if (window.location.hash !== '#thank-you') return;

      const container = document.getElementById(rootId);
      if (container) {
        container.style.visibility = 'hidden';
      }

      window.location.replace(confirmation_page_url);
    };

    handleHashRedirect();
    window.addEventListener('hashchange', handleHashRedirect);

    return () => {
      window.removeEventListener('hashchange', handleHashRedirect);
    };
  }, [confirmation_page_url, rootId]);

  return (
    <>
      <Script
        src="https://assets.makeforms.io/bundles/scripts/live/eu/embed.js"
        strategy="afterInteractive"
        onLoad={() => setScriptLoaded(true)}
        onError={(e) => console.error('Failed to load MakeForms script', e)}
      />

      {formHeader && formInitialized ? (
        <h3 className="form-header mb-4">{formHeader}</h3>
      ) : null}
      <div
        ref={containerRef}
        id={rootId}
        className={`makeforms-embed-container ${className}`}
      />
    </>
  );
}
